import { create } from "zustand";
import { persist } from "zustand/middleware";

type ColorMode = "light" | "dark" | "system";
export type Theme =
  | "orion-blue"
  | "midnight-green"
  | "lemon-tree"
  | "purple-rain"
  | "peppermint"
  | "none";

type ThemeState = {
  colorMode: ColorMode;
  theme: Theme;
  baseFontSize: number;
  baseBorderRadius: number;
};

type Actions = {
  setColorMode: (value: ColorMode) => void;
  setTheme: (value: Theme) => void;
  setBaseFontSize: (value: number) => void;
  setBaseBorderRadius: (value: number) => void;
};

const initialState: ThemeState = {
  colorMode: "system",
  theme: "midnight-green",
  baseFontSize: 16,
  baseBorderRadius: 0.5,
};

// Utility functions to apply styles directly to the <html> element
const root = window?.document.documentElement;

const applyBaseFontSize = (fontSize: number) => {
  root.style.setProperty("--base-font-size", `${fontSize}px`);
};

const applyBaseBorderRadius = (borderRadius: number) => {
  root.style.setProperty("--base-radius", `${borderRadius}rem`);
};

// Keep the PWA chrome (iOS/Android status bar in installed or
// added-to-home-screen mode) in sync with the active theme: mirror the
// body's resolved background color into <meta name="theme-color">, which
// otherwise stays on the hardcoded index.html value.
const syncThemeColorMeta = () => {
  const color = window.getComputedStyle(document.body).backgroundColor;
  if (!color || color === "rgba(0, 0, 0, 0)" || color === "transparent") {
    return;
  }
  let meta = document.querySelector<HTMLMetaElement>(
    'meta[name="theme-color"]',
  );
  if (!meta) {
    meta = document.createElement("meta");
    meta.name = "theme-color";
    document.head.appendChild(meta);
  }
  meta.content = color;
};

const applyColorMode = (mode: ColorMode) => {
  root.classList.remove("dark", "light");
  if (mode === "dark") {
    root.classList.add("dark");
  } else if (mode === "light") {
    root.classList.add("light");
  } else {
    const isDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    root.classList.add(isDark ? "dark" : "light");
  }
  syncThemeColorMeta();
};

const themeClasses: Theme[] = [
  "orion-blue",
  "midnight-green",
  "lemon-tree",
  "purple-rain",
  "peppermint",
  "none",
];

const applyTheme = (theme: Theme) => {
  themeClasses.forEach((c) => root.classList.remove(c));
  root.classList.add(theme);
  syncThemeColorMeta();
};

export const useThemeState = create<ThemeState & Actions>()(
  persist(
    (set) => ({
      // State
      ...initialState,

      // Actions
      setColorMode: (value: ColorMode) => {
        set({ colorMode: value });
        applyColorMode(value);
      },
      setTheme: (value: Theme) => {
        set({ theme: value });
        applyTheme(value);
      },
      setBaseFontSize: (value: number) => {
        set({ baseFontSize: value });
        applyBaseFontSize(value);
      },
      setBaseBorderRadius: (value: number) => {
        set({ baseBorderRadius: value });
        applyBaseBorderRadius(value);
      },

      // RESET
      resetThemeState: () => {
        set(initialState);
      },
    }),
    {
      name: "theme-state",
      onRehydrateStorage: () => (state) => {
        // Apply persisted values
        if (state?.baseFontSize) {
          applyBaseFontSize(state.baseFontSize);
        }
        if (state?.baseBorderRadius) {
          applyBaseBorderRadius(state.baseBorderRadius);
        }
        if (state?.colorMode) {
          applyColorMode(state.colorMode);
        }
        if (state?.theme) {
          applyTheme(state.theme);
        }
      },
      version: 2,
      // v2 rebrand: Midnight Emerald becomes the default. Pre-v2 state has
      // "orion-blue" persisted only because it was the old default — the
      // theme picker shipped in the same release, so nobody has actively
      // chosen it yet. Flip everyone once; Legacy Blue stays selectable.
      migrate: (persistedState, version) => {
        const state = persistedState as ThemeState;
        if (version < 2) {
          return { ...state, theme: "midnight-green" as Theme };
        }
        return state;
      },
    },
  ),
);
